// 表のセル編集で、インライン入力（表の中の <input>）と補助入力（選択時スタイルパネルの入力欄）が
// 共有する状態と操作の受け口。
//
// 入力 DOM を同時に 2 個有効にしないため、草稿・選択位置・履歴・IME・送信キューはすべて
// use-table-cell-editor が 1 つだけ所有する。このストアは「いまの草稿」と「操作の入口」を
// 別コンポーネント（選択時スタイルパネル）へ渡すためだけに使う。

import { create } from "zustand";
import type {
  ChangeEvent,
  ClipboardEvent,
  CompositionEvent,
  FocusEvent,
  FormEvent,
  KeyboardEvent,
  SyntheticEvent,
} from "react";

/** セル間の移動方向。キー操作・補助入力の前後ボタンで共有する。 */
export type TableCellMoveDirection = "next" | "previous" | "down" | "up";

/**
 * セル書式パネルの識別属性。
 *
 * セル編集器は、この属性を持つ要素の内側へフォーカスが移った blur では編集を終了しない
 * （パネルの操作で選択範囲と編集状態が失われないようにする）。パネル側と編集器側で名前が
 * ずれると「押した瞬間に編集が終わる」退行になるため、両者が読むこのモジュールへ集約する。
 */
export const TABLE_CELL_PANEL_ATTRIBUTE = "data-canvas-table-cell-panel";

/** 編集中セルの選択範囲（`start === end` はキャレットのみ）。 */
export interface TableCellSelection {
  readonly start: number;
  readonly end: number;
}

/** 書式パネルを寄せる基準にするセル要素の画面矩形（ビューポート座標）。 */
export interface TableCellAnchorRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

function isSameSelection(a: TableCellSelection | null, b: TableCellSelection | null): boolean {
  if (a === null || b === null) return a === b;
  return a.start === b.start && a.end === b.end;
}

function isSameAnchorRect(a: TableCellAnchorRect | null, b: TableCellAnchorRect | null): boolean {
  if (a === null || b === null) return a === b;
  return a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}

/**
 * 入力欄へ渡すイベントハンドラ群。
 * インライン入力と補助入力が**同じ実装**を使うことで、IME・貼り付けの改行畳み込み・
 * セル内 Undo / Redo・送信キューの扱いが表示方式によってずれないようにする。
 */
export interface TableCellInputHandlers {
  readonly onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  readonly onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  readonly onBeforeInput: (event: FormEvent<HTMLInputElement>) => void;
  readonly onCompositionStart: () => void;
  readonly onCompositionEnd: (event: CompositionEvent<HTMLInputElement>) => void;
  readonly onPaste: (event: ClipboardEvent<HTMLInputElement>) => void;
  readonly onSelect: (event: SyntheticEvent<HTMLInputElement>) => void;
  readonly onBlur: (event: FocusEvent<HTMLInputElement>) => void;
}

/** 補助入力から編集器を操作するための受け口。編集していないときは null。 */
export interface TableCellEditorHandle {
  readonly objectId: string;
  readonly row: number;
  readonly column: number;
  readonly rowCount: number;
  readonly columnCount: number;
  /**
   * 編集位置（row / column）を決めた時点の構造 revision。
   * 書式パネルはこの値を送信へ使う。現在値で組み直すと、他参加者の行・列増減を挟んだときに
   * 同じ添字が指す**別のセル**へ書式が当たる。
   */
  readonly structureRevision: number;
  /** 補助入力欄へ渡すイベントハンドラ群（インライン入力と同一の実装）。 */
  readonly inputHandlers: TableCellInputHandlers;
  /** 前後・上下のセルへ移動する（未送信入力は確定してから移動する）。 */
  readonly moveCell: (direction: TableCellMoveDirection) => void;
  /** 編集を終了する（未送信入力は確定する）。 */
  readonly endEdit: () => void;
  /**
   * 書式パネルからの適用に先立ち、debounce 待ちの本文を送り、そこまでの入力を
   * キャンバス履歴の 1 グループとして閉じる。
   *
   * 本文の確定は、保存済みの本文と画面の草稿を一致させてから範囲を当てるために要る。
   * 履歴を閉じるのは、書式適用の履歴を「そこまでの入力」より後ろへ積むためで、これを行わないと
   * 編集を抜けた後の `Ctrl+Z` が「入力の取り消し → 書式適用の取り消し（本文が戻る）」という
   * 前後した順序になる。
   */
  readonly prepareStyleApply: () => void;
  /**
   * 書式パネルからの適用が成立したことを編集器へ知らせる。
   * 編集中の Ctrl+Z はセル編集器が横取りするため、通知しないと書式の取り消しが
   * 直前の入力の取り消しへ消費される。
   */
  readonly notifyStyleApplied: () => void;
}

interface TableCellEditorState {
  /** 編集中セルの草稿。インライン入力と補助入力が同じ値を見る。 */
  draft: string;
  /**
   * インライン入力では入力・確認ができないため、補助入力へ切り替えているか。
   * 画面上の入力領域が小さすぎる・画面外にある場合に true になる。
   */
  auxiliary: boolean;
  handle: TableCellEditorHandle | null;
  /** 編集中セルの選択範囲。書式パネルの種別（セル用 / 範囲用）と現在値の集計に使う。 */
  selection: TableCellSelection | null;
  /** 編集中セルの画面矩形。書式パネルの配置基準にする。 */
  anchorRect: TableCellAnchorRect | null;
  setDraft: (draft: string) => void;
  setAuxiliary: (auxiliary: boolean) => void;
  setHandle: (handle: TableCellEditorHandle | null) => void;
  setSelection: (selection: TableCellSelection | null) => void;
  setAnchorRect: (rect: TableCellAnchorRect | null) => void;
  reset: () => void;
}

export const useTableCellEditorStore = create<TableCellEditorState>((set) => ({
  draft: "",
  auxiliary: false,
  handle: null,
  selection: null,
  anchorRect: null,
  setDraft: (draft) => set((state) => (state.draft === draft ? state : { draft })),
  setAuxiliary: (auxiliary) =>
    set((state) => (state.auxiliary === auxiliary ? state : { auxiliary })),
  setHandle: (handle) => set({ handle }),
  // 同値での更新を弾く（パネルの再描画と配置計算を、変化が無いときに走らせない）。
  setSelection: (selection) =>
    set((state) => (isSameSelection(state.selection, selection) ? state : { selection })),
  setAnchorRect: (anchorRect) =>
    set((state) => (isSameAnchorRect(state.anchorRect, anchorRect) ? state : { anchorRect })),
  reset: () =>
    set({ draft: "", auxiliary: false, handle: null, selection: null, anchorRect: null }),
}));
